import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarOption } from '../types/survey';
import './CalendarVoting.css';

interface CalendarVotingProps {
  onComplete: (preference: number, ranking: Record<number, number>) => void;
}

const CalendarVoting: React.FC<CalendarVotingProps> = ({ onComplete }) => {
  const [rankings, setRankings] = useState<Record<number, number>>({});
  const [previewOption, setPreviewOption] = useState<CalendarOption | null>(null);
  const [showInstructionModal, setShowInstructionModal] = useState(true);
  const [hasContinued, setHasContinued] = useState(false);
  const [isClient, setIsClient] = useState(() => typeof window !== 'undefined');

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPreviewOption(null);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  const calendarOptions: CalendarOption[] = [
    {
      id: 1,
      title: "Calendar 1",
      image: "https://njesd.b-cdn.net/photos/misc/calendar-survey-2025/test/calendar_1.jpg",
      description: "Currently adopted calendar with early August start"
    },
    {
      id: 2,
      title: "Calendar 2",
      image: "https://njesd.b-cdn.net/photos/misc/calendar-survey-2025/test/calendar_2.jpg",
      description: "Late August start, shorter breaks, move PTC to November, end mid-June."
    },
    {
      id: 3,
      title: "Calendar 3",
      image: "https://njesd.b-cdn.net/photos/misc/calendar-survey-2025/test/calendar_3.jpg",
      description: "Late August start, no fall break, spring break at Easter, end mid-June."
    }
  ];

  const handleRankingChange = (optionId: number, rank: number) => {
    const newRankings = { ...rankings };

    // Remove this rank from any other option
    Object.keys(newRankings).forEach(key => {
      if (newRankings[parseInt(key)] === rank && parseInt(key) !== optionId) {
        delete newRankings[parseInt(key)];
      }
    });

    newRankings[optionId] = rank;
    setRankings(newRankings);
    setHasContinued(false);
  };

  const isRankingComplete = () => {
    const usedRanks = Object.values(rankings);
    return usedRanks.length === 3 &&
           usedRanks.includes(1) &&
           usedRanks.includes(2) &&
           usedRanks.includes(3);
  };

  const handleContinue = () => {
    if (isRankingComplete()) {
      setHasContinued(true);
      const favoriteId = Object.keys(rankings).find(key => rankings[parseInt(key)] === 1);
      onComplete(parseInt(favoriteId!), rankings);
    }
  };

  const getRankingLabel = (rank: number) => {
    switch (rank) {
      case 1: return 'Most Preferred';
      case 2: return 'Second Choice';
      case 3: return 'Least Preferred';
      default: return 'Rank this option';
    }
  };

  const openPreview = (option: CalendarOption) => {
    setPreviewOption(option);
  };

  const closePreview = () => {
    setPreviewOption(null);
  };

  return (
    <div className="calendar-voting">

      <div className="calendar-comparison">
        {calendarOptions.map((option) => (
          <div key={option.id} className="calendar-card">
            <div className="card-image">
              <img
                src={option.image}
                alt={`${option.title} preview`}
                onClick={() => openPreview(option)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openPreview(option);
                  }
                }}
              />
              <button
                type="button"
                className="card-preview-btn"
                onClick={() => openPreview(option)}
              >
                View full size
              </button>
            </div>

            <div className="card-content">
              <h3>{option.title}</h3>
              <p className="card-description">{option.description}</p>

              <div className="ranking-control">
                <label htmlFor={`rank-${option.id}`}>Your ranking:</label>
                <select
                  id={`rank-${option.id}`}
                  value={rankings[option.id] || ''}
                  onChange={(e) => handleRankingChange(option.id, parseInt(e.target.value))}
                  className="rank-select"
                >
                  <option value="">Choose rank</option>
                  <option value="1">1st - Most Preferred</option>
                  <option value="2">2nd - Second Choice</option>
                  <option value="3">3rd - Least Preferred</option>
                </select>
              </div>

              {rankings[option.id] && (
                <div className={`rank-badge vote-badge rank-${rankings[option.id]}`}>
                  <span className="rank-number">{rankings[option.id]}</span>
                  <span className="rank-label">{getRankingLabel(rankings[option.id])}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <p className="section-description">
          Rank the three calendar options from most preferred to least preferred.
      </p>

      {/* Floating Continue Button */}
      {isClient && !hasContinued && isRankingComplete() && createPortal(
        <div className="floating-continue-container">
          <button
            className="floating-continue-btn"
            onClick={handleContinue}
          >
            Continue →
          </button>
        </div>,
        document.body
      )}

      {isClient && previewOption && createPortal(
        <div className="calendar-preview" role="dialog" aria-modal="true" aria-label={`${previewOption.title} full size preview`}>
          <div className="calendar-preview__backdrop" onClick={closePreview} />
          <div className="calendar-preview__content">
            <button type="button" className="calendar-preview__close" onClick={closePreview} aria-label="Close preview">
              ×
            </button>
            <div className="calendar-preview__body">
              <img src={previewOption.image} alt={`${previewOption.title} full size`} />
            </div>
            <div className="calendar-preview__actions">
              <span>{previewOption.title}</span>
              <a href={previewOption.image} target="_blank" rel="noopener noreferrer" className="calendar-preview__open-link">
                Open in new tab
              </a>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showInstructionModal && (
        <div className="instruction-modal" role="dialog" aria-modal="true" aria-label="Calendar ranking instructions">
          <div className="instruction-modal__backdrop" />
          <div className="instruction-modal__content">
            <h2>Rank the three calendar options from most preferred to least preferred</h2>
            <button
              type="button"
              className="instruction-modal__button"
              onClick={() => setShowInstructionModal(false)}
            >
              Let's Begin
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarVoting;
